import { useEffect } from 'react';

import { useObservability } from 'helpers/observability';

import type { StagedPhotosExposureData } from './stagedPhotosEdge';

const SESSION_KEY_PREFIX = 'od-staged-photos-exposure:';

// GrowthBook analyzes each viewer from their first exposure, so one impression per arm in a
// browser session is enough. The in-memory set also covers strict mode remounts and browsers that
// block session storage.
const impressedAssignments = new Set<string>();

export function clearStagedPhotosImpressions() {
  impressedAssignments.clear();
}

function sessionHasImpression(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function rememberSessionImpression(key: string): void {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Without session storage, the in-memory set still limits impressions for this page load.
  }
}

/** Records one impression for the page, for its first home whose shown photos staging changes. */
export const StagedPhotosExposure = ({
  exposure,
}: {
  exposure: StagedPhotosExposureData | null | undefined;
}) => {
  const { trackJourneyEvent } = useObservability();

  useEffect(() => {
    const addressId = exposure?.addressIds[0];
    if (!exposure || !addressId) return;
    const { experiment } = exposure;
    const key = `${SESSION_KEY_PREFIX}${experiment.key}|${experiment.variant}|${experiment.variationId}`;
    if (impressedAssignments.has(key) || sessionHasImpression(key)) return;
    impressedAssignments.add(key);
    rememberSessionImpression(key);
    trackJourneyEvent('experimentation', 'impression', {
      experimentName: experiment.key,
      variant: experiment.variant,
      variationId: experiment.variationId,
      addressToken: addressId,
    });
  }, [exposure, trackJourneyEvent]);

  return null;
};
